import { useMemo, useState } from 'react';

const CaretRight = () => (
  <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" className="filter-caret">
    <path
      d="M8.23666 3.44641L14.3753 9.80581C14.4324 9.86487 14.4773 9.93461 14.5074 10.011C14.5375 10.0874 14.5522 10.169 14.5508 10.2512C14.5493 10.3333 14.5317 10.4143 14.4989 10.4896C14.4661 10.5649 14.4188 10.633 14.3597 10.69L8.00029 16.8287C7.88096 16.9439 7.72076 17.0069 7.55494 17.004C7.38911 17.0011 7.23124 16.9324 7.11605 16.8131C7.00087 16.6938 6.9378 16.5336 6.94073 16.3677C6.94366 16.2019 7.01234 16.044 7.13167 15.9288L13.0419 10.2245L7.33681 4.31503C7.27977 4.25594 7.23493 4.1862 7.20485 4.10979C7.17477 4.03337 7.16003 3.95178 7.16148 3.86967C7.16293 3.78756 7.18054 3.70654 7.2133 3.63124C7.24607 3.55594 7.29334 3.48782 7.35242 3.43079C7.41151 3.37375 7.48125 3.32892 7.55766 3.29883C7.63408 3.26875 7.71567 3.25402 7.79778 3.25547C7.87989 3.25692 7.96091 3.27453 8.03621 3.30729C8.11151 3.34005 8.17963 3.38732 8.23666 3.44641Z"
      fill="#001E93"
      stroke="#001E93"
    />
  </svg>
);

const CaretDown = () => (
  <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.44641 8.23666L9.80581 14.3753C9.86487 14.4324 9.93461 14.4773 10.011 14.5074C10.0874 14.5375 10.169 14.5522 10.2512 14.5508C10.3333 14.5493 10.4143 14.5317 10.4896 14.4989C10.5649 14.4661 10.633 14.4188 10.69 14.3597L16.8287 8.00029C16.9439 7.88096 17.0069 7.72076 17.004 7.55494C17.0011 7.38911 16.9324 7.23124 16.8131 7.11605C16.6938 7.00087 16.5336 6.9378 16.3677 6.94073C16.2019 6.94366 16.044 7.01234 15.9288 7.13167L10.2245 13.0419L4.31503 7.33681C4.25594 7.27977 4.1862 7.23493 4.10979 7.20485C4.03337 7.17477 3.95178 7.16003 3.86967 7.16148C3.78756 7.16293 3.70654 7.18054 3.63124 7.2133C3.55594 7.24607 3.48782 7.29334 3.43079 7.35242C3.37375 7.41151 3.32892 7.48125 3.29883 7.55766C3.26875 7.63408 3.25402 7.71567 3.25547 7.79778C3.25692 7.87989 3.27453 7.96091 3.30729 8.03621C3.34005 8.11151 3.38732 8.17963 3.44641 8.23666Z"
      fill="#001E93"
      stroke="#001E93"
    />
  </svg>
);

/**
 * Extract unique non-empty values from a property field across all items.
 */
function uniqueValues(items, getter) {
  const set = new Set();
  items.forEach((item) => {
    const val = getter(item);
    if (val) set.add(val);
  });
  return [...set].sort();
}

function FilterSection({ label, options, expanded, onToggle, selectedValues, onToggleValue }) {
  if (!options.length) return null;

  return (
    <div className="filter-item">
      <div className="filter-row" onClick={onToggle}>
        <span className="filter-label regular-body">{label}</span>
        {expanded ? <CaretDown /> : <CaretRight />}
      </div>

      {expanded && (
        <div className="filter-expand-box">
          {options.map((opt) => (
            <div className="filter-checkbox-row" key={opt}>
              <input
                type="checkbox"
                id={`filter-${label}-${opt}`}
                checked={selectedValues.includes(opt)}
                onChange={() => onToggleValue(opt)}
              />
              <label htmlFor={`filter-${label}-${opt}`} className="filter-checkbox-label">
                {opt}
              </label>
            </div>
          ))}
        </div>
      )}

      <div className="filter-divider"/>
    </div>
  );
}

export default function FilterSidebar({ filters, onApply, properties = [] }) {
  const [expanded, setExpanded] = useState('city');
  const [localFilters, setLocalFilters] = useState(filters);

  const filterOptions = useMemo(() => ({
    city: uniqueValues(properties, (p) => p.city),
    locality: uniqueValues(properties, (p) => p.location),
    budget: uniqueValues(properties, (p) => p.price),
    propertyType: uniqueValues(properties, (p) => p.propertyType),
    builder: uniqueValues(properties, (p) => p.developer),
    possession: uniqueValues(properties, (p) => p.status),
    configuration: uniqueValues(properties, (p) => p.bhk),
  }), [properties]);

  const toggle = (section) => {
    setExpanded((prev) => (prev === section ? '' : section));
  };

  const toggleValue = (field, value) => {
    setLocalFilters((prev) => {
      const current = prev[field];
      const updated = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [field]: updated };
    });
  };

  const handleClear = () => {
    const cleared = {
      city: [],
      locality: [],
      budget: [],
      propertyType: [],
      builder: [],
      possession: [],
      configuration: [],
    };
    setLocalFilters(cleared);
    onApply(cleared);
  };

  const handleApply = () => {
    onApply(localFilters);
  };

  const sections = [
    { key: 'city', label: 'City', options: filterOptions.city },
    { key: 'locality', label: 'Locality', options: filterOptions.locality },
    { key: 'budget', label: 'Budget Range', options: filterOptions.budget },
    { key: 'propertyType', label: 'Property Type', options: filterOptions.propertyType },
    { key: 'builder', label: 'Builder', options: filterOptions.builder },
    { key: 'possession', label: 'Possession Status', options: filterOptions.possession },
    { key: 'configuration', label: 'Configuration', options: filterOptions.configuration },
  ];

  return (
    <aside className="filter-sidebar">
      <div className="filter-card">
        <h2 className="filter-card-title large-body">Filters</h2>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {sections.map((section) => (
            <FilterSection
              key={section.key}
              label={section.label}
              options={section.options}
              expanded={expanded === section.key}
              onToggle={() => toggle(section.key)}
              selectedValues={localFilters[section.key]}
              onToggleValue={(value) => toggleValue(section.key, value)}
            />
          ))}
        </div>

        <div className="filter-actions">
          <button className="btn-clear" onClick={handleClear}>
            Clear
          </button>
          <button className="btn-apply primary-button btn" onClick={handleApply}>
            Apply
          </button>
        </div>
      </div>
    </aside>
  );
}
